import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { getPremiumUserIds } from "@/lib/premium.server";

function publicClient() {
  return createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { persistSession: false, autoRefreshToken: false } },
  );
}

export type ProfilePublic = {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  city: string | null;
  country: string | null;
  favorite_genres: string[];
  is_premium: boolean;
  counts: {
    owned: number;
    wishlist: number;
    for_sale: number;
    for_trade: number;
    followers: number;
    following: number;
  };
};

export const getProfileByUsername = createServerFn({ method: "GET" })
  .inputValidator((i: { username: string }) => i)
  .handler(async ({ data }): Promise<ProfilePublic | null> => {
    const sb = publicClient();
    const { data: p, error } = await sb
      .from("profiles")
      .select("id, username, display_name, avatar_url, city, country, favorite_genres")
      .eq("username", data.username)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!p) return null;
    const { data: bio } = await sb.rpc("get_profile_bio", { _id: p.id });

    const [albums, followers, following, premIds] = await Promise.all([
      sb.from("user_albums").select("status, listing").eq("user_id", p.id),
      sb.from("follows").select("follower_id", { count: "exact", head: true }).eq("following_id", p.id),
      sb.from("follows").select("following_id", { count: "exact", head: true }).eq("follower_id", p.id),
      getPremiumUserIds([p.id]),
    ]);
    const counts = { owned: 0, wishlist: 0, for_sale: 0, for_trade: 0 };
    (albums.data ?? []).forEach((r: { status: string; listing?: string | null }) => {
      if (r.status === "owned") counts.owned++;
      else if (r.status === "wishlist") counts.wishlist++;
      if (r.listing === "for_sale") counts.for_sale++;
      else if (r.listing === "for_trade") counts.for_trade++;
    });
    return {
      ...p,
      bio: (bio as string | null) ?? null,
      is_premium: premIds.has(p.id),
      counts: {
        ...counts,
        followers: followers.count ?? 0,
        following: following.count ?? 0,
      },
    };
  });

export const getProfileAlbums = createServerFn({ method: "GET" })
  .inputValidator((i: { userId: string; status?: "owned" | "wishlist" | "for_sale" | "for_trade" }) => i)
  .handler(async ({ data }) => {
    const sb = publicClient();
    let q = sb
      .from("user_albums")
      .select("id, discogs_id, title, artist, year, cover_url, genres, status, listing, price_brl, sale_price, condition")
      .eq("user_id", data.userId)
      .order("created_at", { ascending: false });
    if (data.status === "owned" || data.status === "wishlist") q = q.eq("status", data.status);
    else if (data.status === "for_sale" || data.status === "for_trade") q = q.eq("listing", data.status);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return rows ?? [];
  });

export const isFollowing = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: { targetId: string }) => i)
  .handler(async ({ data, context }) => {
    const { data: row } = await context.supabase
      .from("follows")
      .select("follower_id")
      .eq("follower_id", context.userId)
      .eq("following_id", data.targetId)
      .maybeSingle();
    return { following: !!row };
  });

export const followUser = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: { targetId: string }) => i)
  .handler(async ({ data, context }) => {
    if (data.targetId === context.userId) throw new Error("Não dá pra seguir você mesmo");
    const { error } = await context.supabase
      .from("follows")
      .insert({ follower_id: context.userId, following_id: data.targetId });
    if (error && !error.message.includes("duplicate")) throw new Error(error.message);
    return { ok: true };
  });

export const unfollowUser = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: { targetId: string }) => i)
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("follows")
      .delete()
      .eq("follower_id", context.userId)
      .eq("following_id", data.targetId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const isProfileLoved = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: { targetId: string }) => i)
  .handler(async ({ data, context }) => {
    const { data: row } = await context.supabase.from("profile_loves").select("profile_id").eq("profile_id", data.targetId).eq("user_id", context.userId).maybeSingle();
    return { loved: !!row };
  });

export const toggleProfileLove = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: { targetId: string }) => i)
  .handler(async ({ data, context }) => {
    if (data.targetId === context.userId) throw new Error("Não dá para amar o próprio perfil");
    const { data: existing } = await context.supabase.from("profile_loves").select("profile_id").eq("profile_id", data.targetId).eq("user_id", context.userId).maybeSingle();
    if (existing) {
      const { error } = await context.supabase.from("profile_loves").delete().eq("profile_id", data.targetId).eq("user_id", context.userId);
      if (error) throw new Error(error.message);
      return { loved: false };
    }
    const { error } = await context.supabase.from("profile_loves").insert({ profile_id: data.targetId, user_id: context.userId });
    if (error) throw new Error(error.message);
    return { loved: true };
  });

async function fetchProfilesByIds(ids: string[]) {
  if (ids.length === 0) return [];
  const sb = publicClient();
  const { data, error } = await sb
    .from("profiles")
    .select("id, username, display_name, avatar_url")
    .in("id", ids);
  if (error) throw new Error(error.message);
  const premiumIds = await getPremiumUserIds(ids);
  const byId = new Map((data ?? []).map((p) => [p.id, p]));
  return ids
    .map((id) => {
      const p = byId.get(id);
      if (!p) return null;
      return { ...p, is_premium: premiumIds.has(p.id) };
    })
    .filter(Boolean) as Array<{
      id: string;
      username: string;
      display_name: string | null;
      avatar_url: string | null;
      is_premium: boolean;
    }>;
}

export const listFollowers = createServerFn({ method: "GET" })
  .inputValidator((i: { userId: string }) => i)
  .handler(async ({ data }) => {
    const sb = publicClient();
    const { data: rows, error } = await sb
      .from("follows")
      .select("follower_id")
      .eq("following_id", data.userId)
      .limit(200);
    if (error) throw new Error(error.message);
    return fetchProfilesByIds((rows ?? []).map((r) => r.follower_id));
  });

export const listFollowing = createServerFn({ method: "GET" })
  .inputValidator((i: { userId: string }) => i)
  .handler(async ({ data }) => {
    const sb = publicClient();
    const { data: rows, error } = await sb
      .from("follows")
      .select("following_id")
      .eq("follower_id", data.userId)
      .limit(200);
    if (error) throw new Error(error.message);
    return fetchProfilesByIds((rows ?? []).map((r) => r.following_id));
  });


export const searchProfiles = createServerFn({ method: "GET" })
  .inputValidator((i: { q: string }) => i)
  .handler(async ({ data }) => {
    if (!data.q || data.q.length < 2) return [];
    const sb = publicClient();
    const { data: rows } = await sb
      .from("profiles")
      .select("id, username, display_name, avatar_url")
      .or(`username.ilike.%${data.q}%,display_name.ilike.%${data.q}%`)
      .limit(20);
    const profiles = rows ?? [];
    const premiumIds = await getPremiumUserIds(profiles.map((p) => p.id));
    return profiles.map((p) => ({ ...p, is_premium: premiumIds.has(p.id) }));
  });

export type SuggestedProfile = {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  is_premium: boolean;
  albums_count: number;
  activity_count: number;
  is_following?: boolean;
};

/**
 * Sugestões de perfis para seguir.
 * Prioriza quem voltou recentemente e mais interagiu com a comunidade.
 */
export const listSuggestedFollows = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<SuggestedProfile[]> => {
    const { supabase, userId } = context;
    const since = new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString();

    const [following, recentPosts, recentComments, recentLikes, activeProfiles] = await Promise.all([
      supabase.from("follows").select("following_id").eq("follower_id", userId),
      supabase
        .from("community_posts")
        .select("user_id")
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(1000),
      supabase.from("community_post_comments").select("user_id").gte("created_at", since).limit(1000),
      supabase.from("community_post_likes").select("user_id").gte("created_at", since).limit(1000),
      supabase
        .from("profiles")
        .select("id, last_active_at")
        .gte("last_active_at", since)
        .order("last_active_at", { ascending: false })
        .limit(200),
    ]);

    const excluded = new Set<string>([userId, ...(following.data ?? []).map((f) => f.following_id)]);
    const activity = new Map<string, number>();
    const addActivity = (id: string, weight: number) => {
      if (excluded.has(id)) return;
      activity.set(id, (activity.get(id) ?? 0) + weight);
    };
    (recentPosts.data ?? []).forEach((r) => {
      if (excluded.has(r.user_id)) return;
      addActivity(r.user_id, 4);
    });
    (recentComments.data ?? []).forEach((r) => addActivity(r.user_id, 2));
    (recentLikes.data ?? []).forEach((r) => addActivity(r.user_id, 1));
    const now = Date.now();
    (activeProfiles.data ?? []).forEach((r) => {
      const lastActiveAt = r.last_active_at ? new Date(r.last_active_at).getTime() : 0;
      const daysSinceActive = Math.max(0, (now - lastActiveAt) / (24 * 60 * 60 * 1000));
      const recencyScore = daysSinceActive <= 1 ? 3 : daysSinceActive <= 7 ? 2 : daysSinceActive <= 30 ? 1 : 0.5;
      addActivity(r.id, recencyScore);
    });

    let candidateIds = [...activity.keys()];
    // Fallback: se ninguém ativo, sugere perfis quaisquer
    if (candidateIds.length < 8) {
      const { data: anyProfiles } = await supabase
        .from("profiles")
        .select("id")
        .limit(60);
      (anyProfiles ?? []).forEach((p) => {
        if (!excluded.has(p.id) && !activity.has(p.id)) candidateIds.push(p.id);
      });
    }
    if (candidateIds.length === 0) return [];

    // Ordena pela participação recente no Feed; usuários que voltam com frequência
    // permanecem elegíveis mesmo quando ainda publicaram pouco.
    candidateIds = candidateIds
      .map((id) => ({ id, score: activity.get(id) ?? 0 }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 12)
      .map((c) => c.id);

    const [{ data: profiles }, { data: albums }, premiumIds] = await Promise.all([
      supabase
        .from("profiles")
        .select("id, username, display_name, avatar_url")
        .in("id", candidateIds),
      publicClient().from("user_albums").select("user_id").in("user_id", candidateIds).eq("status", "owned"),
      getPremiumUserIds(candidateIds),
    ]);

    const albumsCount = new Map<string, number>();
    (albums ?? []).forEach((a: { user_id: string }) =>
      albumsCount.set(a.user_id, (albumsCount.get(a.user_id) ?? 0) + 1),
    );
    const byId = new Map((profiles ?? []).map((p) => [p.id, p]));

    return candidateIds
      .map((id) => {
        const p = byId.get(id);
        if (!p || !p.username) return null;
        return {
          id: p.id,
          username: p.username,
          display_name: p.display_name,
          avatar_url: p.avatar_url,
          is_premium: premiumIds.has(p.id),
          albums_count: albumsCount.get(p.id) ?? 0,
          activity_count: activity.get(p.id) ?? 0,
        } satisfies SuggestedProfile;
      })
      .filter((x): x is SuggestedProfile => x !== null);
  });

/**
 * Embaixadores: apenas membros Pro ativos, em ordem aleatória a cada visita.
 */
export const listAmbassadors = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<SuggestedProfile[]> => {
    const { supabase, userId } = context;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const [{ data: subs }, { data: following }] = await Promise.all([
      supabaseAdmin
        .from("subscriptions")
        .select("user_id, current_period_end, status")
        .eq("plan", "premium")
        .in("status", ["active", "trialing"]),
      supabase.from("follows").select("following_id").eq("follower_id", userId),
    ]);

    const now = Date.now();
    const followingIds = new Set<string>((following ?? []).map((f) => f.following_id));
    // Mostra todos os Pro (exceto o próprio usuário); quem já é seguido aparece como "Seguindo"
    const proIds = (subs ?? [])
      .filter((s) => !s.current_period_end || new Date(s.current_period_end).getTime() > now)
      .map((s) => s.user_id)
      .filter((id) => id !== userId);

    const shuffle = (arr: string[]) => {
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      return arr;
    };
    shuffle(proIds);

    // Complementa com perfis do ranking: mais XP (ranking) e mais ativos recentemente,
    // sorteados de forma aleatória dentro desse grupo de destaque.
    const proSet = new Set(proIds);
    const [{ data: topXp }, { data: activeProfiles }] = await Promise.all([
      supabase.from("user_xp").select("user_id, xp").order("xp", { ascending: false }).limit(80),
      supabase
        .from("profiles")
        .select("id, last_active_at")
        .order("last_active_at", { ascending: false })
        .limit(80),
    ]);

    const candidates: string[] = [];
    const seen = new Set<string>([userId, ...proSet]);
    for (const row of topXp ?? []) {
      if (row.user_id && !seen.has(row.user_id)) {
        seen.add(row.user_id);
        candidates.push(row.user_id);
      }
    }
    for (const p of activeProfiles ?? []) {
      if (p.id && !seen.has(p.id)) {
        seen.add(p.id);
        candidates.push(p.id);
      }
    }
    const extraIds = shuffle(candidates).slice(0, 24);


    const ids = [...proIds, ...extraIds];
    if (ids.length === 0) return [];

    const [{ data: profiles }, { data: albums }] = await Promise.all([
      supabase
        .from("profiles")
        .select("id, username, display_name, avatar_url")
        .in("id", ids),
      publicClient().from("user_albums").select("user_id").in("user_id", ids).eq("status", "owned"),
    ]);

    const albumsCount = new Map<string, number>();
    (albums ?? []).forEach((a: { user_id: string }) =>
      albumsCount.set(a.user_id, (albumsCount.get(a.user_id) ?? 0) + 1),
    );
    // Remove o perfil oficial da Viny Club da lista de embaixadores
    const OFFICIAL_USERNAMES = new Set(["viny"]);
    const byId = new Map(
      (profiles ?? [])
        .filter((p) => !p.username || !OFFICIAL_USERNAMES.has(p.username.toLowerCase()))
        .map((p) => [p.id, p]),
    );

    // Fora dos embaixadores (Pro), só sugere quem tem mais de 6 discos na coleção
    return ids
      .map((id): SuggestedProfile | null => {
        const p = byId.get(id);
        if (!p || !p.username) return null;
        if (!proSet.has(p.id) && (albumsCount.get(p.id) ?? 0) <= 6) return null;
        return {
          id: p.id,
          username: p.username,
          display_name: p.display_name,
          avatar_url: p.avatar_url,
          is_premium: proSet.has(p.id),
          albums_count: albumsCount.get(p.id) ?? 0,
          activity_count: 0,
          is_following: followingIds.has(p.id),
        } satisfies SuggestedProfile;
      })
      .filter((x): x is SuggestedProfile => x !== null);
  });


